// src/contracts/components/ContractTypeDropdown.js
// Custom-themed dropdown for picking the contract type. Replaces the native
// <select> which can't be styled to match the dark theme (the OS-default
// dropdown menu always looks out of place).
//
// HIC is the only kind that's live; TI / S&M / SF are dimmed with a
// "Coming soon" pill until their templates are built.

import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

const TYPES = [
  { value: 'HIC', label: 'HIC — Home Improvement Contract',     enabled: true },
  { value: 'TI',  label: 'TI — Tenant Improvement',             enabled: false },
  { value: 'SM',  label: 'S&M — Service & Maintenance',         enabled: false },
  { value: 'SF',  label: 'SF — Short-Form',                     enabled: false },
];

export default function ContractTypeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = TYPES.find(t => t.value === value) || TYPES[0];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full bg-gray-950/50 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-indigo-500 flex items-center justify-between gap-2"
      >
        <span className="truncate text-left">{selected.label}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl overflow-hidden">
          {TYPES.map(t => {
            const isSelected = t.value === value;
            const interactive = t.enabled;
            return (
              <button
                key={t.value}
                type="button"
                disabled={!interactive}
                onClick={() => {
                  if (!interactive) return;
                  onChange(t.value);
                  setOpen(false);
                }}
                className={`w-full px-3 py-2 text-sm text-left flex items-center gap-2 border-b border-zinc-800 last:border-b-0 transition-colors ${
                  interactive
                    ? 'text-zinc-200 hover:bg-zinc-800 cursor-pointer'
                    : 'text-zinc-500 cursor-not-allowed bg-zinc-900/50'
                }`}
              >
                <span className="flex-1 truncate">{t.label}</span>
                {isSelected && interactive && (
                  <Check className="w-4 h-4 text-indigo-400" aria-hidden />
                )}
                {!interactive && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-500 border border-zinc-700">
                    Coming soon
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
